import * as THREE from "three";

/** How many recent points along people's (and ships') paths plants feel at once. */
const POINTS = 16;
/** How long a point is felt (s): fronds open back up, and glow fades, over this. */
const FADE = 2.5;
/** A new point every this often (s) per mover. */
const EVERY = 0.12;
/** How far from a point plants bend away (blocks). */
const REACH = 3;

/**
 * Plants that react (see lifeasteroids.md, "Plants that react"): fronds and
 * foliage curl away from whoever passes and slowly open again, and glow
 * plants brighten along their path and fade, leaving a trail of light. All
 * drawing: the shader gets the most recent points along everyone's path (and
 * how fresh each is), so nothing is sent and nothing is simulated.
 */
const touch = { value: Array.from({ length: POINTS }, () => new THREE.Vector3(0, 0, 0)) };
const time = { value: 0 };

const trail: { x: number; y: number; at: number }[] = [];
const lastAt = new Map<string, number>();

/** Note where everything that moves is now (`id` -> world point), at time `now` (s). */
export function touchPlants(movers: Iterable<[string, { x: number; y: number }]>, now: number): void {
  time.value = now;
  for (const [id, p] of movers) {
    if (now - (lastAt.get(id) ?? -Infinity) < EVERY) continue;
    lastAt.set(id, now);
    trail.push({ x: p.x, y: p.y, at: now });
  }
  while (trail.length && (now - trail[0]!.at > FADE || trail.length > POINTS * 4)) trail.shift();
  // The freshest points, strongest first.
  const fresh = trail.slice(-POINTS);
  for (let i = 0; i < POINTS; i++) {
    const t = fresh[fresh.length - 1 - i];
    touch.value[i]!.set(t?.x ?? 0, t?.y ?? 0, t ? Math.max(0, 1 - (now - t.at) / FADE) : 0);
  }
}

const TOUCH_GLSL = /* glsl */ `
uniform vec3 uTouch[${POINTS}];
uniform float uTime;
/** How much people have touched near world point p lately: the push away from them (xy) and the strength (z). */
vec3 touchAt(vec2 p) {
  vec2 push = vec2(0.0);
  float strength = 0.0;
  for (int i = 0; i < ${POINTS}; i++) {
    vec3 t = uTouch[i];
    vec2 d = p - t.xy;
    float l = length(d);
    if (t.z <= 0.0 || l > ${REACH.toFixed(1)}) continue;
    float k = (1.0 - l / ${REACH.toFixed(1)}) * t.z;
    push += normalize(d + vec2(1e-4)) * k;
    strength = max(strength, k);
  }
  return vec3(push, strength);
}
`;

/** Make a tile material bend the tiles marked bendable (attribute aBend) away from what passes, and sway a little. */
export function bendWithTouch(material: THREE.Material): void {
  material.onBeforeCompile = (shader) => {
    shader.uniforms.uTouch = touch;
    shader.uniforms.uTime = time;
    shader.vertexShader = shader.vertexShader
      .replace("#include <common>", `#include <common>\nattribute float aBend;\n${TOUCH_GLSL}`)
      .replace(
        "#include <begin_vertex>",
        `#include <begin_vertex>
        if (aBend > 0.0) {
          vec4 wp = modelMatrix * vec4(transformed, 1.0);
          vec3 t = touchAt(wp.xy);
          transformed.xy += (t.xy * 0.55 + vec2(sin(uTime * 1.3 + wp.x * 0.4 + wp.y * 0.3), cos(uTime * 1.1 + wp.y * 0.35)) * 0.05) * aBend;
        }`,
      );
  };
  material.customProgramCacheKey = () => "bend";
}

/** Make a glow material brighten near where people have passed lately. */
export function glowWithTouch(material: THREE.Material): void {
  material.onBeforeCompile = (shader) => {
    shader.uniforms.uTouch = touch;
    shader.uniforms.uTime = time;
    shader.vertexShader = shader.vertexShader
      .replace("#include <common>", `#include <common>\n${TOUCH_GLSL}`)
      .replace(
        "#include <color_vertex>",
        `#include <color_vertex>
        #ifdef USE_COLOR
          vColor.rgb *= 1.0 + 1.8 * touchAt((modelMatrix * vec4(position, 1.0)).xy).z;
        #endif`,
      );
  };
  material.customProgramCacheKey = () => "glow-touch";
}
